import ApiKnowledgeBase from "../../../api/knowledgeBase";

export const KNOWLEDGE_BASE_IMAGE_PREFIX = "fybrpedia/images/";
export const KNOWLEDGE_BASE_VIDEO_PREFIX = "fybrpedia/videos/";

export function extractImageUrls(content: any): string[] {
  const urls = new Set<string>();

  const walk = (node: any) => {
    if (!node || typeof node !== "object") return;

    if (node.type === "image" && node.attrs?.src) {
      urls.add(node.attrs.src);
    }

    if (Array.isArray(node.content)) {
      node.content.forEach(walk);
    }
  };

  walk(content);
  return Array.from(urls);
}

export function extractVideoUrls(content: any): string[] {
  const urls = new Set<string>();

  const walk = (node: any) => {
    if (!node || typeof node !== "object") return;

    if (node.type === "video" && node.attrs?.src) {
      urls.add(node.attrs.src);
    }

    if (Array.isArray(node.content)) {
      node.content.forEach(walk);
    }
  };

  walk(content);
  return Array.from(urls);
}

function urlToKey(url: string): string | null {
  if (!url) return null;

  try {
    const parsed = new URL(url);
    const path = parsed.pathname.startsWith("/")
      ? parsed.pathname.slice(1)
      : parsed.pathname;

    if (
      path.startsWith(KNOWLEDGE_BASE_IMAGE_PREFIX) ||
      path.startsWith(KNOWLEDGE_BASE_VIDEO_PREFIX)
    ) {
      return path;
    }

    return null;
  } catch {
    if (
      url.startsWith(KNOWLEDGE_BASE_IMAGE_PREFIX) ||
      url.startsWith(KNOWLEDGE_BASE_VIDEO_PREFIX)
    ) {
      return url;
    }
    return null;
  }
}

function keyToType(key: string): "image" | "video" | null {
  if (key.startsWith(KNOWLEDGE_BASE_IMAGE_PREFIX)) return "image";
  if (key.startsWith(KNOWLEDGE_BASE_VIDEO_PREFIX)) return "video";
  return null;
}

export async function resolveImageUrlsToSigned(
  content: any
): Promise<Record<string, string>> {
  const urls = new Set<string>([
    ...extractImageUrls(content),
    ...extractVideoUrls(content),
  ]);
  const result: Record<string, string> = {};

  const keyRequests = Array.from(urls)
    .map((url) => {
      const key = urlToKey(url);
      const type = key ? keyToType(key) : null;
      return { url, key, type };
    })
    .filter(
      (item): item is { url: string; key: string; type: "image" | "video" } =>
        item.key !== null && item.type !== null
    );

  await Promise.all(
    keyRequests.map(async ({ url, key, type }) => {
      try {
        if (type === "image") {
          const { downloadUrl } = await ApiKnowledgeBase.getImageDownloadUrl(
            key
          );
          result[url] = downloadUrl;
        } else {
          const { downloadUrl } = await ApiKnowledgeBase.getVideoDownloadUrl(
            key
          );
          result[url] = downloadUrl;
        }
      } catch (error) {
        console.error(
          "Failed to create presigned download url",
          key,
          type,
          error
        );
      }
    })
  );

  return result;
}

export async function preloadImages(urls: string[]): Promise<Set<string>> {
  const loaded = new Set<string>();

  await Promise.all(
    urls.map(
      (url) =>
        new Promise<void>((resolve) => {
          const img = new Image();
          img.onload = () => {
            loaded.add(url);
            resolve();
          };
          img.onerror = () => resolve();
          img.src = url;
        })
    )
  );

  return loaded;
}

export function replaceImageUrls(content: any, urlMap: Record<string, string>) {
  if (!urlMap || Object.keys(urlMap).length === 0) return content;

  const cloned = JSON.parse(JSON.stringify(content));

  const walk = (node: any) => {
    if (!node || typeof node !== "object") return;

    if (node.attrs?.src && urlMap[node.attrs.src]) {
      node.attrs.src = urlMap[node.attrs.src];
    }

    if (Array.isArray(node.content)) {
      node.content.forEach(walk);
    }
  };

  walk(cloned);
  return cloned;
}
